import { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Grid,
    Divider,
    Paper,
    Card,
    CardContent,
    CardActionArea,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Alert,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SendIcon from '@mui/icons-material/Send';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getTooltip } from '../../constants/tooltips/emailTooltips';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// WI-11437: Fusion brand palette for the config-list marking
const BRAND = {
    navy: '#003d52',  // primary / main
    teal: '#00afaa',  // accent
    red: '#ed1c24'    // accent (destructive)
};

// WI-11437: multi-recipient admin list shown when a configuration is flagged
// "Set System Notification" — replaces the single Admin Address field.
function AdminRecipientsEditor({ recipients, adminUserEmails, onChange }) {
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState('');

    const addRecipient = (rawEmail) => {
        const email = (rawEmail || '').trim();
        if (!email) return false;

        if (!EMAIL_PATTERN.test(email)) {
            setInputError('Please enter a valid email address.');
            return false;
        }
        if (recipients.some(e => e.toLowerCase() === email.toLowerCase())) {
            setInputError('This email address has already been added.');
            return false;
        }

        onChange([...recipients, email]);
        setInputError('');
        return true;
    };

    const handleAdd = () => {
        if (addRecipient(inputValue)) {
            setInputValue('');
        }
    };

    const availableAdminEmails = (adminUserEmails || []).filter(
        e => !recipients.some(r => r.toLowerCase() === e.toLowerCase())
    );
    const noAdminOptions = availableAdminEmails.length === 0;

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Admin Recipients
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1 }}>
                <TextField
                    select
                    size="small"
                    label="Admin users"
                    value=""
                    disabled={noAdminOptions}
                    onChange={(e) => addRecipient(e.target.value)}
                    sx={{ minWidth: 220 }}
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: () => noAdminOptions
                            ? 'No admin emails available'
                            : 'Select an admin user'
                    }}
                    InputLabelProps={{ shrink: true }}
                >
                    {availableAdminEmails.map((email) => (
                        <MenuItem key={email} value={email}>
                            {email}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    label="Email address"
                    placeholder="Type an email address"
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        if (inputError) setInputError('');
                    }}
                    error={!!inputError}
                    helperText={inputError}
                    sx={{ flexGrow: 1, minWidth: 220 }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                />
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    disabled={!inputValue.trim()}
                >
                    Add
                </Button>
            </Box>
            {recipients.length === 0 ? (
                <Typography variant="body2" color="error">
                    At least one admin recipient is required.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {recipients.map((email) => (
                        <Chip
                            key={email}
                            label={email}
                            size="small"
                            onDelete={() => onChange(recipients.filter(e => e !== email))}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

// Helper component for field with tooltip
function FieldWithTooltip({ tooltip, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            {/* minWidth: 0 lets the TextField shrink with its column instead of spilling under the help icon */}
            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
            <Tooltip
                title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{tooltip}</Typography>}
                arrow
                placement="top"
            >
                <HelpOutlineIcon
                    fontSize="small"
                    sx={{ color: 'action.active', mt: 1, cursor: 'help' }}
                />
            </Tooltip>
        </Box>
    );
}

const PROVIDER_TYPES = {
    onprem: [
        { value: 'SMTP',           label: 'SMTP' },
        { value: 'Office365',      label: 'Office365 (Basic Auth)' },
        { value: 'MicrosoftGraph', label: 'Microsoft Graph' },
    ],
    cloud: [
        { value: 'MicrosoftGraph', label: 'Microsoft Graph' },
        { value: 'SendGrid',       label: 'SendGrid' },
    ],
};

function EmailConfigSection({
    smtpConfig,
    smtpSettings,
    selectedIndex,
    formData,
    formHasChanges,
    saving,
    testLoading,
    hasSageConfig = false,
    x3Folders = [],
    onFormChange,
    onSelectConfig,
    onSave,
    onDeleteClick,
    onTest,
    onEnableMultiSmtp,
    isFormValid,
    deploymentType = 'onprem',
    adminUserEmails = []
}) {
    const availableTypes = PROVIDER_TYPES[deploymentType] ?? PROVIDER_TYPES.onprem;
    const currentTypeAllowed = !formData.type || availableTypes.some(t => t.value === formData.type);
    // If no SMTP config document exists, show enable button
    if (!smtpConfig) {
        return (
            <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                    Email Configuration
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Email configuration is not enabled. Enable it to manage multiple email configurations for different folders.
                </Alert>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onEnableMultiSmtp}
                >
                    Enable Email Configuration
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                Email Configuration
            </Typography>

            {!hasSageConfig && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Sage X3 settings must be configured before adding email configurations. Please configure Sage X3 connection details first.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 3 }}>
                {/* Left Panel - Config List */}
                <Box sx={{ width: 280, flexShrink: 0 }}>
                    <Button
                        fullWidth
                        variant={selectedIndex === null ? 'contained' : 'outlined'}
                        startIcon={<AddIcon />}
                        onClick={() => onSelectConfig(null)}
                        sx={{ mb: 2 }}
                    >
                        Add New Configuration
                    </Button>

                    {smtpSettings.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                            No configurations yet
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {/* WI-11437: the System Notification config is always listed first.
                                Handlers keep the original smtpSettings index, which the API uses as id. */}
                            {smtpSettings
                                .map((config, index) => ({ config, index }))
                                .sort((a, b) => (b.config.isSystemNotification === true) - (a.config.isSystemNotification === true))
                                .map(({ config, index }) => (
                                <Card
                                    key={config.settingsid || index}
                                    variant="outlined"
                                    sx={{
                                        borderColor: selectedIndex === index
                                            ? BRAND.navy
                                            : (config.isSystemNotification ? BRAND.teal : 'divider'),
                                        borderWidth: (selectedIndex === index || config.isSystemNotification) ? 2 : 1,
                                        backgroundColor: selectedIndex === index ? 'action.selected' : 'background.paper'
                                    }}
                                >
                                    <CardActionArea onClick={() => onSelectConfig(index)}>
                                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                            {config.isSystemNotification && (
                                                <Chip
                                                    icon={<NotificationsIcon />}
                                                    label="System Notification"
                                                    size="small"
                                                    sx={{
                                                        mb: 1,
                                                        fontWeight: 600,
                                                        bgcolor: BRAND.navy,
                                                        color: '#fff',
                                                        '& .MuiChip-icon': { color: BRAND.teal }
                                                    }}
                                                />
                                            )}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                                                        {config.emailConfig || 'Unnamed Config'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                        {config.type} • {config.folder}
                                                    </Typography>
                                                    {config.host && (
                                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                            {config.host}:{config.port}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                                                    {config.isDefault && (
                                                        <Tooltip title="Default for folder">
                                                            <CheckCircleIcon color="success" fontSize="small" />
                                                        </Tooltip>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); onDeleteClick(index); }}
                                                        sx={{ color: BRAND.red }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Right Panel - Form. The form sits beside the 280px config list (and the
                    240px Configuration nav), so MUI's viewport-based md breakpoint (>=900px)
                    left each column ~120-150px wide at half-screen. Two columns only from lg. */}
                {/* Right Panel - Form */}
                <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        {selectedIndex !== null ? 'Edit Configuration' : 'New Configuration'}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <FieldWithTooltip tooltip={getTooltip('configName', formData.type)}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Configuration Name"
                                    value={formData.emailConfig || ''}
                                    onChange={(e) => onFormChange('emailConfig', e.target.value)}
                                    required
                                />
                            </FieldWithTooltip>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <FieldWithTooltip tooltip={getTooltip('serverType', formData.type)}>
                                <FormControl fullWidth size="small" required>
                                    <InputLabel>Server Type</InputLabel>
                                    <Select
                                        value={formData.type || ''}
                                        label="Server Type"
                                        onChange={(e) => onFormChange('type', e.target.value)}
                                    >
                                        <MenuItem value="">Select type...</MenuItem>
                                        {!currentTypeAllowed && (
                                            <MenuItem value={formData.type} disabled>
                                                {formData.type} (not available for this deployment)
                                            </MenuItem>
                                        )}
                                        {availableTypes.map(({ value, label }) => (
                                            <MenuItem key={value} value={value}>{label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </FieldWithTooltip>
                        </Grid>

                        {/* SMTP / Office365 fields */}
                        {formData.type && formData.type !== 'MicrosoftGraph' && formData.type !== 'SendGrid' && (
                            <>
                                <Grid item xs={12} lg={8}>
                                    <FieldWithTooltip tooltip={getTooltip('host', formData.type)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Server Host / IP"
                                            value={formData.host || ''}
                                            onChange={(e) => onFormChange('host', e.target.value)}
                                            required
                                            autoComplete="off"
                                            inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                                        />
                                    </FieldWithTooltip>
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <FieldWithTooltip tooltip={getTooltip('port', formData.type)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Port"
                                            type="number"
                                            value={formData.port || ''}
                                            onChange={(e) => onFormChange('port', e.target.value)}
                                            required
                                            autoComplete="off"
                                            inputProps={{ min: 1, max: 65535, autoComplete: 'off', 'data-form-type': 'other' }}
                                        />
                                    </FieldWithTooltip>
                                </Grid>
                            </>
                        )}

                        {/* Microsoft Graph fields */}
                        {formData.type === 'MicrosoftGraph' && (
                            <>
                                <Grid item xs={12}>
                                    <FieldWithTooltip tooltip={getTooltip('tenantId', formData.type)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Tenant ID"
                                            value={formData.tenantId || ''}
                                            onChange={(e) => onFormChange('tenantId', e.target.value)}
                                            required
                                            helperText="Azure AD Directory (tenant) ID"
                                            autoComplete="off"
                                            inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                                        />
                                    </FieldWithTooltip>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <FieldWithTooltip tooltip={getTooltip('clientId', formData.type)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Application (Client) ID"
                                            value={formData.clientId || ''}
                                            onChange={(e) => onFormChange('clientId', e.target.value)}
                                            required
                                            autoComplete="off"
                                            inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                                        />
                                    </FieldWithTooltip>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <FieldWithTooltip tooltip={getTooltip('clientSecret', formData.type)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Client Secret"
                                            type="password"
                                            value={formData.clientSecret || ''}
                                            onChange={(e) => onFormChange('clientSecret', e.target.value)}
                                            required
                                            autoComplete="new-password"
                                            inputProps={{ autoComplete: 'new-password', 'data-form-type': 'other' }}
                                        />
                                    </FieldWithTooltip>
                                </Grid>
                            </>
                        )}

                        {/* SendGrid fields */}
                        {formData.type === 'SendGrid' && (
                            <Grid item xs={12}>
                                <FieldWithTooltip tooltip={getTooltip('apiKey', formData.type)}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="API Key"
                                        type="password"
                                        value={formData.apiKey || ''}
                                        onChange={(e) => onFormChange('apiKey', e.target.value)}
                                        required
                                        helperText="Your SendGrid API key — requires Mail Send permission"
                                        autoComplete="new-password"
                                        inputProps={{ autoComplete: 'new-password', 'data-form-type': 'other' }}
                                    />
                                </FieldWithTooltip>
                            </Grid>
                        )}

                        <Grid item xs={12} lg={6}>
                            <FieldWithTooltip tooltip={getTooltip('folder', formData.type)}>
                                <FormControl fullWidth size="small" required>
                                    <InputLabel>Sage X3 Folder</InputLabel>
                                    <Select
                                        value={formData.folder || ''}
                                        label="Sage X3 Folder"
                                        onChange={(e) => onFormChange('folder', e.target.value)}
                                    >
                                        <MenuItem value="">Select folder...</MenuItem>
                                        {x3Folders.map((folder) => (
                                            <MenuItem key={folder} value={folder}>
                                                {folder}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </FieldWithTooltip>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isDefault || false}
                                            onChange={(e) => onFormChange('isDefault', e.target.checked)}
                                        />
                                    }
                                    label="Set as folder default"
                                />
                                <Tooltip title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{getTooltip('isDefault', formData.type)}</Typography>} arrow>
                                    <HelpOutlineIcon fontSize="small" sx={{ color: 'action.active', ml: -1, cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isSystemNotification || false}
                                            onChange={(e) => onFormChange('isSystemNotification', e.target.checked)}
                                        />
                                    }
                                    label="Set System Notification"
                                />
                                <Tooltip title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{getTooltip('isSystemNotification', formData.type)}</Typography>} arrow>
                                    <HelpOutlineIcon fontSize="small" sx={{ color: 'action.active', ml: -1, cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                        </Grid>

                        {/* Security Options for SMTP (not Office365, Graph, or SendGrid) */}
                        {formData.type && formData.type !== 'MicrosoftGraph' && formData.type !== 'Office365' && formData.type !== 'SendGrid' && (
                            <>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="caption" color="text.secondary">Security Options</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.secure || false} onChange={(e) => onFormChange('secure', e.target.checked)} />}
                                            label="Secure (SSL/TLS)"
                                        />
                                        <Tooltip title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{getTooltip('secure', formData.type)}</Typography>} arrow>
                                            <HelpOutlineIcon fontSize="small" sx={{ color: 'action.active', ml: -1, cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.ignoreTLS || false} onChange={(e) => onFormChange('ignoreTLS', e.target.checked)} />}
                                            label="Ignore TLS"
                                        />
                                        <Tooltip title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{getTooltip('ignoreTLS', formData.type)}</Typography>} arrow>
                                            <HelpOutlineIcon fontSize="small" sx={{ color: 'action.active', ml: -1, cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.requireTLS || false} onChange={(e) => onFormChange('requireTLS', e.target.checked)} />}
                                            label="Require TLS"
                                        />
                                        <Tooltip title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{getTooltip('requireTLS', formData.type)}</Typography>} arrow>
                                            <HelpOutlineIcon fontSize="small" sx={{ color: 'action.active', ml: -1, cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <FieldWithTooltip tooltip={getTooltip('tlsServerName', formData.type)}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="TLS Server Name"
                                            value={formData.tlsServerName || ''}
                                            onChange={(e) => onFormChange('tlsServerName', e.target.value)}
                                            helperText="Override server name for TLS certificate validation"
                                            autoComplete="off"
                                            inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                                        />
                                    </FieldWithTooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.bypassProxy || false} onChange={(e) => onFormChange('bypassProxy', e.target.checked)} />}
                                            label="Bypass Proxy"
                                        />
                                        <Tooltip title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{getTooltip('bypassProxy', formData.type)}</Typography>} arrow>
                                            <HelpOutlineIcon fontSize="small" sx={{ color: 'action.active', ml: -1, cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            </>
                        )}

                        {/* Authentication & Addresses */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" color="text.secondary">Authentication & Addresses</Typography>
                        </Grid>
                        {formData.type !== 'SendGrid' && (
                            <Grid item xs={12} lg={6}>
                                <FieldWithTooltip tooltip={getTooltip('username', formData.type)}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Username"
                                        value={formData.username || ''}
                                        onChange={(e) => onFormChange('username', e.target.value)}
                                        required={formData.type !== 'SMTP'}
                                        autoComplete="new-password"
                                        inputProps={{ autoComplete: 'new-password', 'data-form-type': 'other' }}
                                    />
                                </FieldWithTooltip>
                            </Grid>
                        )}
                        {formData.type !== 'MicrosoftGraph' && formData.type !== 'SendGrid' && (
                            <Grid item xs={12} lg={6}>
                                <FieldWithTooltip tooltip={getTooltip('password', formData.type)}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Password"
                                        type="password"
                                        value={formData.password || ''}
                                        onChange={(e) => onFormChange('password', e.target.value)}
                                        required={formData.type === 'Office365'}
                                        autoComplete="new-password"
                                        inputProps={{ autoComplete: 'new-password', 'data-form-type': 'other' }}
                                    />
                                </FieldWithTooltip>
                            </Grid>
                        )}

                        <Grid item xs={12} lg={6}>
                            <FieldWithTooltip tooltip={getTooltip('fromAddress', formData.type)}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="From Address"
                                    value={formData.emailFrom || ''}
                                    onChange={(e) => onFormChange('emailFrom', e.target.value)}
                                    required
                                    helperText="e.g., info@domain.com or Name <info@domain.com>"
                                    autoComplete="off"
                                    inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                                />
                            </FieldWithTooltip>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <FieldWithTooltip tooltip={getTooltip('replyTo', formData.type)}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Reply-To Address"
                                    value={formData.replyTo || ''}
                                    onChange={(e) => onFormChange('replyTo', e.target.value)}
                                    autoComplete="off"
                                    inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                                />
                            </FieldWithTooltip>
                        </Grid>
                        {formData.isSystemNotification ? (
                            <Grid item xs={12}>
                                <FieldWithTooltip tooltip={getTooltip('adminRecipients', formData.type)}>
                                    <AdminRecipientsEditor
                                        recipients={Array.isArray(formData.adminAddresses) ? formData.adminAddresses : []}
                                        adminUserEmails={adminUserEmails}
                                        onChange={(recipients) => onFormChange('adminAddresses', recipients)}
                                    />
                                </FieldWithTooltip>
                            </Grid>
                        ) : (
                            <Grid item xs={12} lg={6}>
                                <FieldWithTooltip tooltip={getTooltip('adminAddress', formData.type)}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Admin Address"
                                        value={formData.admin_address || ''}
                                        onChange={(e) => onFormChange('admin_address', e.target.value)}
                                        required
                                        helperText="For test emails and notifications"
                                        autoComplete="off"
                                        inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                                    />
                                </FieldWithTooltip>
                            </Grid>
                        )}
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            startIcon={testLoading ? <CircularProgress size={16} /> : <SendIcon />}
                            onClick={onTest}
                            disabled={!isFormValid || testLoading}
                        >
                            Send Test Email
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onSave}
                            disabled={!isFormValid || saving}
                        >
                            {saving ? <CircularProgress size={20} /> : (selectedIndex !== null ? 'Update' : 'Save')}
                        </Button>
                    </Box>
                </Paper>
            </Box>

        </Box>
    );
}

export default EmailConfigSection;